import _ from 'lodash';
import {generateCardGrouping} from 'lib/protocolHelper';
import {CardType, TaskDelayUnit, TaskType, TrayTypeKey, TrayType, NormalizationType} from '../../constants/dataEnum';
import {findKeyByValue} from '../../lib/objectHelper';
import {CsvCellKeysEnum, CSV_CELL_KEYS, ESCAPE_CHAR, isIgnoredCell} from '../../lib/protocolConversionHelper';

const parseToCharacterLiteral = (value) => {
  let newValue = value;

  if (value && typeof value === 'string') {
    newValue = newValue.replaceAll(ESCAPE_CHAR.QUOTE.string, ESCAPE_CHAR.QUOTE.char);
    newValue = newValue.replaceAll(ESCAPE_CHAR.COMA.string, ESCAPE_CHAR.COMA.char);
  }

  return newValue;
};

const generateSerialDilutionCardObject = (sdCard) => {
  const {ID, sdRawTasksSummary, sdRawTasks, sdProperty} = sdCard;

  const {
    taskDiluentSource: diluentSourceID,
    taskSampleSource: sampleSourceID,
    taskSampleTarget: sampleTargetIDs,
    taskDiluentTargets: diluentTargetIDs,
  } = sdRawTasksSummary;

  const sampleConcentrationIds = (sampleTargetIDs || []).map((id) => sdRawTasks[id]?.processedTaskId);
  const diluentTargetIds = (diluentTargetIDs || []).map((id) => sdRawTasks[id]?.processedTaskId);

  return {
    cardId: ID,
    reuseTips: sdProperty.reuseTips,
    step: sdProperty.step,
    diluentSourceId: sdRawTasks[diluentSourceID]?.processedTaskId || null,
    sampleSourceId: sdRawTasks[sampleSourceID]?.processedTaskId || null,
    sampleConcentrationIds,
    diluentTargetProperties: JSON.stringify(sdProperty.stepProperties),
    isSetSampleToAllSteps: sdProperty.isSetSampleToAllSteps || false,
    diluentTargetIds,
    replicateWell: sdProperty.replicateWell || 1,
  };
};

const generateNormalizationCardObject = (normalizationCard) => {
  const {normalizationState, ID} = normalizationCard;
  const {
    csvRawData,
    csvFileName,
    currentVolume,
    delimiter,
    targetConcentration,
    wellIncrementPattern,
    selectedColumnIndex,
    startRow,
    type,
    diluentLabwareID,
    sampleLabwareID,
    diluentWell,
  } = normalizationState || {};

  return {
    cardId: ID,
    csvRawData,
    csvFileName,
    csvSelectedIndex: [startRow, selectedColumnIndex],
    targetConcentration,
    wellIncrementPattern,
    currentVolume,
    delimiter,
    type: type || NormalizationType.FIXED_SAMPLE_VOLUME,
    sampleLabwareId: Number(sampleLabwareID),
    diluentLabwareId: Number(diluentLabwareID),
    diluentWellPosition: [diluentWell.row, diluentWell.column],
  };
};

// will be used for CreateProtocol and UpdateProtocol
export const generateCardObject = (cardState, previousCardObject) => {
  const {cards} = cardState;

  const cardObject = {};
  const SerialDilution = {};
  const Normalization = {};

  Object.values(cards).forEach((card) => {
    const {ID, groupID, name, type, tasks: cardTasks, subType} = card || {};

    const isNewCard = previousCardObject ? !(previousCardObject.card && previousCardObject.card[ID]) : true;

    cardObject[ID] = {
      ID: isNewCard ? null : previousCardObject.card[ID].id,
      cardId: ID,
      groupID,
      name,
      type,
      subType,
      tasks: cardTasks,
    };

    if (card.type === CardType.SERIAL_DILUTION) {
      SerialDilution[ID] = generateSerialDilutionCardObject(card);
      SerialDilution[ID].ID = isNewCard ? null : previousCardObject.serialDilution[ID]?.id;
    }

    if (card.type === CardType.NORMALIZATION) {
      Normalization[ID] = generateNormalizationCardObject(card);
      Normalization[ID].ID = isNewCard ? null : previousCardObject.normalization[ID]?.id;
    }

    if (card.type === CardType.BROADCAST || card.type === CardType.POOLING || card.type === CardType.MIXING) {
      cardObject[ID].isReuseTips = card.reuseTips;
    }
  });

  return {
    Card: _.isEmpty(cardObject) ? null : cardObject,
    SerialDilution: _.isEmpty(SerialDilution) ? null : SerialDilution,
    Normalization: _.isEmpty(Normalization) ? null : Normalization,
  };
};

export const convertCardStateToCsv =
  (
    // eslint-disable-next-line default-param-last
    labwareParameter = undefined,
    trayType,
  ) =>
  (dispatch, getState) => {
    console.log('convertCardStateToCsv');
    const {cardState, labwareState: currentLabwareState, protocolState} = getState();
    const {cards, tasks, groups} = cardState;
    const labwareState = labwareParameter ?? currentLabwareState;

    const {lastOpenedProtocol} = protocolState;
    const {isEjectToCaddy} = lastOpenedProtocol || {};
    const protocolTrayType = trayType || lastOpenedProtocol?.trayType || TrayType.NORMAL_CADDY;

    const csvTaskRows = [];
    const csvHumidifierTaskRow = [];

    const plateListCells = Array(CSV_CELL_KEYS.length).fill('""');
    plateListCells[0] = `"${'PlateList'}"`;

    // set plate id
    Object.keys(labwareState).forEach((labwareKey, i) => {
      const labware = labwareState[labwareKey];
      let id = '';

      if (labware.IDPrefix) {
        id = labware.IDPrefix;
      }

      plateListCells[i + 1] = `"${id}"`;
    });

    // set plate barcode
    Object.keys(labwareState).forEach((labwareKey, i) => {
      const labware = labwareState[labwareKey];
      if (labware.LabwareID) {
        let name = '';

        if (labware.IDPrefix) {
          name = parseToCharacterLiteral(labware.name);
        }

        const barcode = labware?.barcode || '';

        plateListCells[i + 7] = `"${name}"`;
        plateListCells[i + 13] = `"${barcode}"`;
      }
    });

    // set tray type
    const trayKey = protocolTrayType;

    plateListCells[19] = TrayTypeKey[trayKey];
    const plateListTaskRow = plateListCells.join(',');

    const groupedCardState = generateCardGrouping(cards, groups);

    groupedCardState.forEach((group) => {
      const cardList = group?.cards || [group.card];

      cardList.forEach((card) => {
        card.tasks.forEach((taskKey) => {
          const task = tasks[taskKey];
          const csvCells = [];
          if (!task) return;
          const {type} = task;

          CSV_CELL_KEYS.forEach((cellKey) => {
            let csvCellValue = '';

            if (type === TaskType.INSERT_TASK) {
              return;
            }

            if (isIgnoredCell(cellKey, type)) {
              csvCellValue = '';
            } else if (type === TaskType.DELAY && cellKey === 'delayTime') {
              const {delayUnit} = task;
              csvCellValue =
                delayUnit === TaskDelayUnit.MINUTE ? `${parseFloat(task[cellKey]) * 60}` : task[cellKey].toString();
            } else if (cellKey === 'location') {
              const isSetEjectToCaddy = type === TaskType.EJECT_TIP && isEjectToCaddy;

              if (card.type === CardType.CONSOLIDATE_TIPS || isSetEjectToCaddy) {
                csvCellValue = '1';
              } else {
                let location = '';
                if (!task.labwareID) {
                  location = '';
                } else if (
                  protocolTrayType === TrayType.DOUBLE_CADDY &&
                  (task.labwareID === 2 || task.labwareID === 1)
                ) {
                  location = task.labwareID.toString();
                } else {
                  location = task.labwareID === 2 ? '2' : findKeyByValue(labwareState, {LabwareID: task.labwareID});
                }

                csvCellValue = location || '';
              }
            } else if (cellKey === CsvCellKeysEnum.MIXING_VOLUME) {
              csvCellValue = task[cellKey] ? task[cellKey].toString() : '';
            } else if (cellKey === 'mixingCycle') {
              csvCellValue = task.enableMixing ? task[cellKey].toString() : '';
            } else if (cellKey === 'ID') {
              csvCellValue = task[cellKey] || task[cellKey] === 0 ? task[cellKey].toString() : '';
            } else if (cellKey === 'isIgnored') {
              if (card.invalidTypes?.length || card.isIgnored) {
                csvCellValue = 'true';
              } else {
                csvCellValue = task[cellKey] ? task[cellKey].toString() : '';
              }
            } else if (cellKey === 'isSimpleTransfer') {
              csvCellValue = card.type === CardType.SIMPLE_TRANSFER ? 'true' : '';
            } else if (cellKey === 'cardID') {
              csvCellValue = card.ID.toString();
            } else if (cellKey === 'cardName') {
              csvCellValue = card.name.toString();
            } else if (cellKey === 'changeTip') {
              csvCellValue = type === TaskType.EJECT_TIP ? `${!!task.changeTip}` : '';
            } else if (cellKey === 'groupID') {
              csvCellValue = card?.groupID?.toString() ?? '';
            } else if (cellKey === 'groupName') {
              const group = groups[card?.groupID];
              csvCellValue = group?.name ? parseToCharacterLiteral(group?.name) : '';
            } else if (cellKey === 'message') {
              const value = task[cellKey];
              csvCellValue = value ? parseToCharacterLiteral(value) : '';
            } else if (type === TaskType.ENABLE_HUMIDIFIER) {
              const {humidifierTimeout, humidityLevel, type} = task;

              if (cellKey === CsvCellKeysEnum.TYPE) csvCellValue = type?.toString() ?? '';
              if (cellKey === CsvCellKeysEnum.DELAY_TIME) csvCellValue = humidifierTimeout?.toString() ?? '';
              else if (cellKey === CsvCellKeysEnum.VOLUME) csvCellValue = humidityLevel?.toString() ?? '';
            } else {
              csvCellValue = task[cellKey] || task[cellKey] === 0 ? task[cellKey].toString() : '';
            }

            csvCellValue = csvCellValue.replace(/"/g, '""');
            csvCells.push(`"${csvCellValue}"`);
          });

          const csvRow = csvCells.join(',');
          if (type === TaskType.ENABLE_HUMIDIFIER) {
            csvHumidifierTaskRow.push(csvRow);
          } else {
            csvTaskRows.push(csvRow);
          }
        });
      });
    });

    const csv = [plateListTaskRow, ...csvHumidifierTaskRow, ...csvTaskRows];
    console.log('tasks', csv);

    return csv.join('\n');
  };
